export const PROTECTED_ORDER_AUTO_REFRESH_ACTION_CODE = "preserve_or_release_manual_order";
export const LEGACY_PROTECTED_ORDER_AUTO_REFRESH_REASON_CODE = "visible_order_sequence_preservation_failed";

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeWarningSequence(values) {
  return (Array.isArray(values) ? values : [])
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
}

function getExcludedTruckRefreshItems(resultsData) {
  return Array.isArray(resultsData?.excluded_truck_refresh?.items)
    ? resultsData.excluded_truck_refresh.items
    : [];
}

export function isProtectedOrderAutoRefreshEligibleWarning(item = {}) {
  if (!item || typeof item !== "object" || normalizeInlineText(item.status).toLowerCase() !== "failed") {
    return false;
  }

  if (item.action_code != null && typeof item.action_code !== "string") {
    return false;
  }

  const actionCode = normalizeInlineText(item.action_code);
  if (actionCode) {
    return actionCode === PROTECTED_ORDER_AUTO_REFRESH_ACTION_CODE;
  }

  return normalizeInlineText(item.reason_code)
    === LEGACY_PROTECTED_ORDER_AUTO_REFRESH_REASON_CODE;
}

export function buildProtectedOrderAutoRefreshWarningId(item = {}) {
  const warningId = normalizeInlineText(item?.warning_id);
  if (warningId) {
    return warningId;
  }

  const truckId = normalizeInlineText(item?.truck_id);
  return truckId ? `excluded-truck-refresh:${truckId}` : "";
}

export function buildProtectedOrderAutoRefreshWarningVersion(resultsData = {}, item = {}) {
  const planningSummary = resultsData?.planning_summary && typeof resultsData.planning_summary === "object"
    ? resultsData.planning_summary
    : {};
  const reasonContext = item?.reason_context && typeof item.reason_context === "object"
    ? item.reason_context
    : {};

  return JSON.stringify([
    normalizeInlineText(resultsData?.generated_at),
    normalizeInlineText(planningSummary.state_updated_at || planningSummary.updated_at),
    normalizeInlineText(item?.truck_id),
    normalizeInlineText(item?.status).toLowerCase(),
    normalizeInlineText(item?.action_code),
    normalizeInlineText(item?.reason_code),
    normalizeInlineText(item?.reason_stage),
    normalizeWarningSequence(reasonContext.expected_order_ids),
    normalizeWarningSequence(reasonContext.actual_order_ids),
  ]);
}

export function buildProtectedOrderAutoRefreshAttemptKey({
  dispatchDate = "",
  truckId = "",
  warningVersion = "",
} = {}) {
  const normalizedDispatchDate = normalizeInlineText(dispatchDate);
  const normalizedTruckId = normalizeInlineText(truckId);
  const normalizedWarningVersion = normalizeInlineText(warningVersion);
  if (!normalizedDispatchDate || !normalizedTruckId || !normalizedWarningVersion) {
    return "";
  }

  return JSON.stringify([normalizedDispatchDate, normalizedTruckId, normalizedWarningVersion]);
}

export function selectProtectedOrderAutoRefreshCandidates(resultsData = {}, { dispatchDate = "" } = {}) {
  const normalizedDispatchDate = normalizeInlineText(dispatchDate);
  if (!normalizedDispatchDate) {
    return [];
  }

  return getExcludedTruckRefreshItems(resultsData)
    .filter((item) => isProtectedOrderAutoRefreshEligibleWarning(item))
    .map((item) => {
      const truckId = normalizeInlineText(item.truck_id);
      const warningId = buildProtectedOrderAutoRefreshWarningId(item);
      const warningVersion = buildProtectedOrderAutoRefreshWarningVersion(resultsData, item);
      const attemptKey = buildProtectedOrderAutoRefreshAttemptKey({
        dispatchDate: normalizedDispatchDate,
        truckId,
        warningVersion,
      });

      if (!truckId || !warningId || !attemptKey) {
        return null;
      }

      return {
        attemptKey,
        truckId,
        truckName: normalizeInlineText(item.truck_name),
        warningId,
        warningVersion,
      };
    })
    .filter(Boolean)
    .sort((left, right) => (
      left.truckId.localeCompare(right.truckId)
      || left.warningId.localeCompare(right.warningId)
    ));
}

export function buildExcludedTruckRefreshWarningsFingerprint(resultsData = {}) {
  const warningRecords = getExcludedTruckRefreshItems(resultsData)
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const warningId = buildProtectedOrderAutoRefreshWarningId(item);
      if (!warningId) {
        return null;
      }
      return [warningId, buildProtectedOrderAutoRefreshWarningVersion(resultsData, item)];
    })
    .filter(Boolean)
    .sort((left, right) => (
      left[0].localeCompare(right[0])
      || left[1].localeCompare(right[1])
    ));

  return JSON.stringify(warningRecords);
}

// L'« Actualiser » automatique attend la première vérification M-à-J et les publications de la
// journée affichée (réussies ou en échec) : sinon il viserait un camion publié ou un verrou pris.
// settledScopes garde, d'un rendu à l'autre, la dernière journée dont chaque chargement a abouti.
export function isProtectedOrderAutoRefreshWaitingForFirstChecks(settledScopes, {
  scopeKey = "",
  sourceChanges = null,
  portalPublications = null,
  canVerifySourceChanges = false,
  canPublishPortal = false,
} = {}) {
  if (`${sourceChanges?.dispatchDate}|${sourceChanges?.dispatchPeriod}` === scopeKey
    && (sourceChanges.hasVerified === true || Boolean(sourceChanges.errorMessage) || Boolean(sourceChanges.unavailableMessage))) {
    settledScopes.sourceChanges = scopeKey;
  }
  if (`${portalPublications?.dispatchDate}|${portalPublications?.dispatchPeriod}` === scopeKey
    && (portalPublications.isLoaded === true || Boolean(portalPublications.errorMessage))) {
    settledScopes.portalPublications = scopeKey;
  }
  return (canVerifySourceChanges && settledScopes.sourceChanges !== scopeKey)
    || (canPublishPortal && settledScopes.portalPublications !== scopeKey);
}
